import {useEffect, CSSProperties, ReactNode, useCallback} from 'react';
import ReactModal from 'react-modal';
import {
  IS_CLIENT,
  ROOT_ELEMENT_ID,
  DISABLE_PASSIVE_TOUCH_IF_POSSIBLE,
} from '../constant';
import usePrevious from '../hook/usePrevious';
import {DEFAULT_ANIMATION_DURATION, ZIndex} from '../scss/util/variable';
import {
  getIsRouteToParams,
  subscribeRouteCancel,
} from '../util/PageRouter/routeCancelObserver';

interface IProps {
  isOpen: boolean;
  // overlay 영역을 터치했을때 호출된다
  // (event: (MouseEvent | KeyboardEvent)) => void
  onRequestClose: any;
  onAfterClose?: () => void;
  onAfterOpen?: () => void;
  // overlay를 제외하고 실제 내용이 들어가는 영역의 스타일
  contentStyle?: CSSProperties;
  overlayStyle?: CSSProperties;
  children: ReactNode;
  shouldCloseOnOverlayClick?: boolean;
  contentRef?: (ref?: HTMLDivElement) => void;
  className?: {
    base: string;
    afterOpen: string;
    beforeClose: string;
  };
  isPreventTouchEvent?: boolean;
}

export default function Modal({
  children,
  isOpen,
  onRequestClose,
  onAfterClose: _onAfterClose,
  onAfterOpen,
  contentStyle,
  overlayStyle = FULL_OVERLAY_STYLE,
  shouldCloseOnOverlayClick = true,
  contentRef,
  className,
  isPreventTouchEvent = true,
}: IProps) {
  const style = {
    overlay: overlayStyle,
    content: contentStyle || DEFAULT_CONTENT_STYLE,
  };

  // 모달이 노출될 때 fixed 때문에 스크롤이 상단으로 가지기 때문에, body에 relative 스타일을 추가합니다.
  useEffect(() => {
    if (isOpen) {
      window.document.body.classList.add('relative');
    }
  }, [isOpen]);

  // 타이밍 이슈로 인해 useEffect return 할 때 말고 onAfterClose에서 relative를 제거합니다.
  const onAfterClose = useCallback(() => {
    window.document.body.classList.remove('relative');
    _onAfterClose?.();
  }, [_onAfterClose]);

  // 실제로 모달이 닫히기 전에 children이 변경되어 보이는 것을 방지하기위해
  // 이전 children을 기억했다가 isOpen이 false인 경우에 사용한다.
  const prevChildren = usePrevious(children);

  useEffect(() => {
    return subscribeRouteCancel((_, params) => {
      // routeBack 만 검사한다
      if (isOpen && !getIsRouteToParams(params)) {
        onRequestClose();
        return true;
      } else {
        return false;
      }
    });
  }, [isOpen, onRequestClose]);

  useEffect(() => {
    if (!isPreventTouchEvent) return;
    /**
     * 모달 내부에서 스크롤이 필요하다면 해당 태그에 touchmove 이벤트를 바인딩하고 e.stopPropagation()을 추가해야한다.
     * 그렇지 않으면 window로 이벤트가 전파(버블링)되고 아래의 e.preventDefault()가 실행되서 기본동작(scroll)이 안되는 문제가 발생한다.
     */
    const handleTouchMove = (e: TouchEvent) => {
      if (isOpen) {
        e.preventDefault();
      }
    };
    window.addEventListener(
      'touchmove',
      handleTouchMove,
      DISABLE_PASSIVE_TOUCH_IF_POSSIBLE
    );
    return () => {
      window.removeEventListener(
        'touchmove',
        handleTouchMove,
        DISABLE_PASSIVE_TOUCH_IF_POSSIBLE
      );
    };
  }, [isOpen, isPreventTouchEvent]);
  return (
    <ReactModal
      isOpen={isOpen}
      onAfterOpen={onAfterOpen}
      contentLabel="PageModal"
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      onRequestClose={onRequestClose}
      onAfterClose={onAfterClose}
      closeTimeoutMS={DEFAULT_ANIMATION_DURATION}
      style={style}
      contentRef={contentRef}
      className={className}
    >
      {isOpen ? children : prevChildren}
    </ReactModal>
  );
}

if (IS_CLIENT && !window.IS_UNITTEST) {
  // 현재는 이 파일이 어디서든 import될 수 있기 때문에 Layout을 사용하지 않는 페이지에서는 루트 컴포넌트에 각각 id="root" 붙여줘야함. 더 나은 방법이 있으면 적용하자.
  ReactModal.setAppElement(`#${ROOT_ELEMENT_ID}`);
}

const DEFAULT_CONTENT_STYLE: CSSProperties = {
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  transform: 'translate(-50%, -50%)',
  padding: 0,
  width: '300px',
  borderRadius: '5px',
  boxShadow: '0 4px 8px 0 rgba(var(--any-black), 0.8)',
  backgroundColor: 'rgb(var(--any-white))',
};

export const FULL_OVERLAY_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: ZIndex.Modal,
  backgroundColor: 'rgba(var(--black-white), 0.7)',
};

export const FULL_CONTENT_STYLE: CSSProperties = {
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  padding: 0,
  border: 0,
  borderRadius: 0,
  backgroundColor: 'rgb(var(--background))',
};
